"use client";

import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	type AnchorProps,
	type AnchorVariantProps,
	anchor,
	type ButtonVariantProps,
	button,
	type CircleButtonProps,
	cn,
	icon,
	type SquareButtonProps,
	Typography,
} from "@tosize/ui";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import NextLink from "next/link";
import { useRouter } from "next/navigation";
import {
	type AnchorHTMLAttributes,
	type ComponentProps,
	type MouseEventHandler,
	useCallback,
	useMemo,
	useRef,
} from "react";
import type { ShortLinkButtonProps } from "~/app/[lang]/overlay/service/ShortLinkButton";
import { useLocale } from "~/app/[lang]/providers/LocaleContext";

type NextLinkProps = ComponentProps<typeof NextLink>;

export interface LinkProps extends Omit<NextLinkProps, "href"> {
	href?: string;
}

const PRELOAD_DELAY = 50;

export function Link({ prefetch = false, href = "#", ...props }: LinkProps) {
	const { lang, domain } = useLocale();
	const router = useRouter();
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const prefix = lang !== domain.defaultLocale || domain.alwaysPrefix ? `/${lang}` : "";

	const url = useMemo(() => {
		if (href.startsWith("#")) {
			return href;
		}

		if (/^(https?:\/\/(www\.)?|mailto:|tel:)/.test(href)) {
			return href;
		}

		if (!href.startsWith(prefix)) {
			return `${prefix}${href}`;
		}

		return href;
	}, [href, prefix]);

	const doPreload = useCallback(() => {
		router.prefetch(url, { kind: PrefetchKind.FULL });
	}, [router, url]);

	const handleEnter: MouseEventHandler = () => {
		timeoutRef.current = setTimeout(() => {
			doPreload();
		}, PRELOAD_DELAY);
	};

	const handleLeave: MouseEventHandler = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	if (typeof href === "undefined") {
		return null;
	}

	return (
		<NextLink
			prefetch={prefetch}
			onTouchStart={() => {
				doPreload();
			}}
			onFocus={() => {
				doPreload();
			}}
			onMouseLeave={handleLeave}
			onMouseEnter={handleEnter}
			href={url}
			{...props}
		/>
	);
}

type StyledLinkProps = AnchorProps & LinkProps;

export function StyledLink(props: StyledLinkProps) {
	const {
		variant = "link",
		color,
		size,
		starticon,
		endicon,
		iconColor,
		iconSize,
		iconClassName,
		className,
		children,
		...remainingProps
	} = props;

	return (
		<Link
			className={cn(
				variant === "link"
					? anchor({ color: color as AnchorVariantProps["color"] })
					: button({ color: color as ButtonVariantProps["color"], size, variant }),
				"cursor-pointer",
				className,
			)}
			rel={props.target === "_blank" && !props.rel ? "noopener" : props.rel}
			{...remainingProps}
		>
			{starticon && (
				<FontAwesomeIcon
					className={cn(icon({ color: iconColor }), { "mr-2": children }, iconClassName)}
					icon={starticon}
					size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
					fixedWidth
				/>
			)}

			{children}

			{endicon && (
				<FontAwesomeIcon
					className={cn(icon({ color: iconColor }), { "ml-2": children })}
					icon={endicon}
					size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
					fixedWidth
				/>
			)}
		</Link>
	);
}

export function SquareLink(
	props: Omit<SquareButtonProps, "component"> & AnchorHTMLAttributes<HTMLAnchorElement>,
) {
	const {
		icon,
		color = "shades-light",
		size = "medium",
		className,
		children,
		...remainingProps
	} = props;

	return (
		<Link
			className={cn(
				"flex shrink-0 appearance-none items-center justify-center rounded p-1 focus:outline-none motion-safe:transition sm:p-2",
				{
					"h-8 w-8 text-xl": size === "small",
					"h-10 w-10 text-2xl": size === "medium",
					"h-12 w-12 text-4xl": size === "large",
					"hover:bg-shades focus-visible:bg-shades": color === "shades",
					"hover:bg-shades-light focus-visible:bg-shades-light": color === "shades-light",
				},
				className,
			)}
			{...remainingProps}
		>
			{children}
			{icon && <FontAwesomeIcon icon={icon} fixedWidth />}
		</Link>
	);
}

export function CircleLink(
	props: Omit<CircleButtonProps, "component"> & AnchorHTMLAttributes<HTMLAnchorElement>,
) {
	const { color = "primary-medium", icon = faCircleInfo, className, ...remainingProps } = props;

	return (
		<Link
			className={cn(
				"inline-flex appearance-none items-center justify-center rounded-full hover:ring-1 hover:ring-offset-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 motion-safe:transition",
				{
					"text-primary-medium hover:ring-primary-medium focus-visible:ring-primary-medium":
						color === "primary-medium",
					"text-shades-medium hover:ring-shades-medium focus-visible:ring-shades-medium":
						color === "shades-medium",
				},
				className,
			)}
			{...remainingProps}
		>
			<FontAwesomeIcon icon={icon} />
		</Link>
	);
}

export function ShortLink(
	props: Omit<ShortLinkButtonProps, "component"> &
		Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> &
		LinkProps,
) {
	const { title, subtitle, loading, className, ...remainingProps } = props;

	return (
		<Link
			title={title}
			className={cn("group cursor-pointer appearance-none focus:outline-none", className)}
			{...remainingProps}
		>
			<div className="flex items-baseline justify-between rounded border border-primary px-4 py-2 child:leading-tight group-hover:bg-primary-light group-focus-visible:bg-primary-light motion-safe:transition">
				<Typography
					variant="paragraph"
					size="text-sm"
					className="mr-2 inline-flex items-center justify-center"
				>
					{loading && (
						<FontAwesomeIcon icon={faSpinnerThird} className="mr-2 animate-spin" fixedWidth />
					)}

					<span>{title}</span>
				</Typography>

				{!!subtitle && (
					<Typography
						variant="paragraph-italic"
						size="text-xs"
						className="whitespace-nowrap text-secondary"
					>
						{subtitle}
					</Typography>
				)}
			</div>
		</Link>
	);
}
